// PhotoViewer (job page round): one photo full size on the dialog layer (Layer, size fill), for the office
// checking the work against the invoice. Its first line is the photo's own note and where it is in the
// set ("4 of 8"); beside the photo, who took it and when, which visit and stage, and whether the
// customer sees it; under it, every photo, small, the one shown drawn heavier.
// - Moving: ‹ › on the photo, ← → on the keyboard, Home and End, a press on any photo in the row, or a
//   swipe on a touch screen.
// - Looking closely: Fit shows the whole photo (a tall one stands upright); Actual size shows it at its
//   own pixels and scrolls. A press on the photo switches between them, keeping the spot pressed in view.
// - Markup: a photo the tech drew on opens with the markup on; one press shows the original.
// - Waiting and failing: a dashed frame while the file loads (DESIGN.md §2, waiting); one that fails says
//   so, with Try again.
// Layer keeps focus inside, closes on Escape or Close (its top right), and returns focus to what opened
// it (the thumbnail). Reads only --viewer-*; the controls in it read their own.
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type MouseEvent } from 'react';
import { Checkbox } from '../Checkbox/Checkbox.tsx';
import { IconButton } from '../IconButton/IconButton.tsx';
import { Layer } from '../Layer/Layer.tsx';
import { Link } from '../Link/Link.tsx';
import { PhotoThumb, type Photo } from '../PhotoThumb/PhotoThumb.tsx';
import { Segmented } from '../Segmented/Segmented.tsx';
import './PhotoViewer.css';

export interface PhotoViewerProps {
  /** The photos it pages through, in order; one still uploading (`waiting`) is left out. */
  photos: Photo[];
  /** The one it opens at; null when it's closed. */
  openId: string | null;
  onClose: () => void;
  /** It moved to another photo (the page may mark that thumbnail). */
  onChange?: (photoId: string) => void;
  /**
   * Given, the office may change who sees a photo: a checkbox, "The customer can see this". Without
   * it the viewer only says who sees it (a mirrored company's photos are its source's to change).
   */
  onShareChange?: (photoId: string, shared: boolean) => void;
  /** Its name for screen readers ("Job 24612’s photos"). */
  label?: string;
  /** Storybook only: draws the stage waiting, as a slow file does. */
  previewState?: 'loading';
}

type Load = 'loading' | 'loaded' | 'failed';
/** How far a finger moves sideways before it counts as a swipe, in CSS px. */
const SWIPE = 48;
/** How long a file may take before the dashed "Loading" frame shows, so a fast one never flashes it. */
const LOADING_AFTER_MS = 250;

export function PhotoViewer({
  photos: given,
  openId,
  onClose,
  onChange,
  onShareChange,
  label = 'The photos',
  previewState,
}: PhotoViewerProps) {
  // one still on its way up has nothing to show yet (PhotoThumb says "Uploading…")
  const photos = useMemo(() => given.filter((p) => !p.waiting), [given]);
  const isOpen = openId !== null && photos.some((p) => p.id === openId);
  const [index, setIndex] = useState(0);
  const [markup, setMarkup] = useState(true);
  const [size, setSize] = useState<'fit' | 'actual'>('fit');
  const [load, setLoad] = useState<Load>('loading');
  const [slow, setSlow] = useState(false);
  const [retry, setRetry] = useState(0);
  const stage = useRef<HTMLDivElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const zoomAt = useRef<{ fx: number; fy: number } | null>(null);

  // opening (or reopening at another photo) starts there, fitted, with the markup on
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  if (isOpen && openId !== openedAt) {
    setOpenedAt(openId);
    setIndex(
      Math.max(
        0,
        photos.findIndex((p) => p.id === openId),
      ),
    );
    setMarkup(true);
    setSize('fit');
  }
  if (!isOpen && openedAt !== null) setOpenedAt(null);

  const n = photos.length;
  const photo = photos[Math.min(index, n - 1)];
  const src = photo ? (markup && photo.markupSrc ? photo.markupSrc : photo.src) : '';
  const shownSrc = retry ? `${src}${src.includes('?') ? '&' : '?'}try=${retry}` : src;

  // a new file starts loading; the dashed frame waits a moment, so a fast file never flashes it
  const [loadKey, setLoadKey] = useState('');
  if (shownSrc !== loadKey) {
    setLoadKey(shownSrc);
    setLoad('loading');
    setSlow(false);
  }
  useEffect(() => {
    if (load !== 'loading') return;
    const t = setTimeout(() => setSlow(true), LOADING_AFTER_MS);
    return () => clearTimeout(t);
  }, [load, loadKey]);

  const go = useCallback(
    (to: number) => {
      if (!n) return;
      const next = (to + n) % n;
      setIndex(next);
      setSize('fit');
      setRetry(0);
      onChange?.(photos[next]!.id);
    },
    [n, photos, onChange],
  );

  // the keys, while it's open; a control that moves with the arrows itself (a segmented switch) keeps them
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target as Element | null;
      if (t?.closest('[role="radiogroup"], [role="radio"], input, textarea, select')) return;
      if (t === stage.current && size === 'actual') return;
      const keys: Record<string, () => void> = {
        ArrowLeft: () => go(index - 1),
        ArrowRight: () => go(index + 1),
        Home: () => go(0),
        End: () => go(n - 1),
      };
      const act = keys[e.key];
      if (!act || n < 2) return;
      e.preventDefault();
      act();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, go, index, n, size]);

  // the row of every photo keeps the one shown in view (the row alone scrolls, never the dialog)
  useEffect(() => {
    const row = strip.current;
    const cur = row?.querySelector('[aria-current="true"]');
    if (!isOpen || !row || !cur) return;
    const r = row.getBoundingClientRect();
    const c = cur.getBoundingClientRect();
    if (c.left < r.left) row.scrollLeft -= r.left - c.left + c.width;
    else if (c.right > r.right) row.scrollLeft += c.right - r.right + c.width;
  }, [isOpen, index]);

  // after switching to actual size, scroll so the spot that was pressed is in the middle
  useEffect(() => {
    const el = stage.current;
    const at = zoomAt.current;
    if (!el || size !== 'actual' || !at || load !== 'loaded') return;
    zoomAt.current = null;
    el.scrollLeft = at.fx * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = at.fy * el.scrollHeight - el.clientHeight / 2;
  }, [size, load]);

  if (!photo) return null;

  const onPhotoPress = (e: MouseEvent<HTMLImageElement>) => {
    if (size === 'fit') {
      const r = e.currentTarget.getBoundingClientRect();
      zoomAt.current = { fx: (e.clientX - r.left) / r.width, fy: (e.clientY - r.top) / r.height };
      setSize('actual');
    } else setSize('fit');
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    swipe.current = e.pointerType === 'mouse' || size !== 'fit' ? null : { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(e.clientY - s.y)) go(dx < 0 ? index + 1 : index - 1);
  };

  const waiting = previewState === 'loading' || (load === 'loading' && slow);
  return (
    <Layer level="dialog" size="fill" label={label} isOpen={isOpen} onClose={onClose}>
      <div className="fl-viewer__head">
        <h2 className="fl-viewer__title">{photo.caption}</h2>
        {n > 1 ? (
          <span className="fl-viewer__count" aria-live="polite">
            {index + 1} of {n}
          </span>
        ) : null}
      </div>
      <div className="fl-viewer__main">
        <div className="fl-viewer__pic">
          <div
            ref={stage}
            className="fl-viewer__stage"
            data-size={size}
            // at actual size the photo scrolls: the keyboard can reach it, and its arrows scroll it
            tabIndex={size === 'actual' ? 0 : undefined}
            role={size === 'actual' ? 'region' : undefined}
            aria-label={size === 'actual' ? 'The photo at actual size: scroll to look around' : undefined}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={() => (swipe.current = null)}
          >
            {load === 'failed' ? (
              <div className="fl-viewer__wait" data-failed="">
                <span>This photo didn’t load.</span>
                <Link onPress={() => setRetry((r) => r + 1)}>Try again</Link>
              </div>
            ) : (
              <img
                key={shownSrc}
                className="fl-viewer__img"
                data-loaded={load === 'loaded' && previewState !== 'loading' ? '' : undefined}
                src={shownSrc}
                alt={photo.caption}
                draggable={false}
                onLoad={() => setLoad('loaded')}
                onError={() => setLoad('failed')}
                onClick={onPhotoPress}
              />
            )}
            {waiting && load !== 'failed' ? (
              <div className="fl-viewer__wait" data-loading="" role="status">
                Loading the photo…
              </div>
            ) : null}
          </div>
          {n > 1 ? (
            <>
              <span className="fl-viewer__nav" data-side="prev">
                <IconButton icon="chevron-left" label="Previous photo" onPress={() => go(index - 1)} />
              </span>
              <span className="fl-viewer__nav" data-side="next">
                <IconButton icon="chevron-right" label="Next photo" onPress={() => go(index + 1)} />
              </span>
            </>
          ) : null}
        </div>
        <div className="fl-viewer__info">
          {photo.by || photo.where ? (
            <div className="fl-viewer__facts">
              {photo.by ? <p className="fl-viewer__fact">{photo.by}</p> : null}
              {photo.where ? <p className="fl-viewer__fact">{photo.where}</p> : null}
            </div>
          ) : null}
          <div className="fl-viewer__who">
            {onShareChange ? (
              <Checkbox isSelected={!photo.officeOnly} onChange={(on) => onShareChange(photo.id, on)}>
                The customer can see this
              </Checkbox>
            ) : (
              <p className="fl-viewer__label">
                {photo.officeOnly ? 'Office only' : 'The customer can see this'}
              </p>
            )}
            <p className="fl-viewer__note">
              {photo.officeOnly
                ? 'Only the office sees it.'
                : 'It shows on their invoice and in the customer portal.'}
            </p>
          </div>
          {photo.markupSrc ? (
            <Segmented
              label="Markup"
              selectedKey={markup ? 'on' : 'off'}
              onSelectionChange={(k) => setMarkup(k === 'on')}
              items={[
                { id: 'on', label: 'Markup' },
                { id: 'off', label: 'Original' },
              ]}
            />
          ) : null}
          <Segmented
            label="Size"
            selectedKey={size}
            onSelectionChange={(k) => setSize(k === 'actual' ? 'actual' : 'fit')}
            items={[
              { id: 'fit', label: 'Fit' },
              { id: 'actual', label: 'Actual size' },
            ]}
          />
          {n > 1 ? <p className="fl-viewer__hint">← → to move · Esc to close</p> : null}
        </div>
      </div>
      {n > 1 ? (
        <div ref={strip} className="fl-viewer__strip" role="group" aria-label="Every photo">
          {photos.map((p, i) => (
            <PhotoThumb key={p.id} photo={p} size="sm" isSelected={i === index} onPress={() => go(i)} />
          ))}
        </div>
      ) : null}
    </Layer>
  );
}
